import { useMemo, useState } from 'react'
import {
  brouillonDe, construireCorps, lireBrouillon, memeValeur,
  type Brouillon, type Corps, type Edition, type ReglagePublie, type ValeurReglage,
} from './modele.js'

/**
 * L'UNITÉ D'ÉDITION d'un écran de réglages : les brouillons des lignes
 * touchées seulement (une ligne jamais touchée suit le serveur), les remises
 * au défaut, et le corps qui partirait. Une seule unité par écran (Save Bar).
 */
export interface UniteEdition {
  edition: Edition
  serveur: Map<string, ValeurReglage>
  corps: Corps
  /** Lignes modifiées ou invalides (compteur de la Save Bar). */
  compte: number
  brouillon: (s: ReglagePublie) => Brouillon
  modifie: (s: ReglagePublie) => boolean
  erreur: (s: ReglagePublie) => string | null
  changer: (s: ReglagePublie, b: Brouillon) => void
  remettre: (s: ReglagePublie) => void
  annuler: (s: ReglagePublie) => void
  vider: () => void
}

const VIDE: Edition = { brouillons: {}, remises: [] }

export function useUniteEdition(reglages: ReglagePublie[], valeurs: ValeurReglage[] | null): UniteEdition {
  const [edition, setEdition] = useState<Edition>(VIDE)
  const serveur = useMemo(() => new Map((valeurs ?? []).map((v) => [v.key, v])), [valeurs])
  const corps = useMemo(() => construireCorps(reglages, serveur, edition), [reglages, serveur, edition])

  const invalides = Object.entries(edition.brouillons).filter(([k, b]) => {
    const s = reglages.find((x) => x.key === k)
    return s ? !lireBrouillon(s, b).ok : false
  }).length

  const brouillon = (s: ReglagePublie): Brouillon => {
    // `null` est une vraie valeur d'un réglage nullable : jamais remplacée par le défaut.
    const v = serveur.get(s.key)
    if (edition.remises.includes(s.key)) return brouillonDe(s, v ? v.default : s.default)
    return edition.brouillons[s.key] ?? brouillonDe(s, v ? v.value : s.default)
  }
  const modifie = (s: ReglagePublie): boolean => {
    if (edition.remises.includes(s.key)) return !(serveur.get(s.key)?.is_default ?? true)
    const b = edition.brouillons[s.key]
    if (!b) return false
    const lu = lireBrouillon(s, b)
    return !lu.ok || !memeValeur(s, lu.value, serveur.get(s.key)?.value)
  }
  const erreur = (s: ReglagePublie): string | null => {
    const b = edition.brouillons[s.key]
    if (!b) return null
    const lu = lireBrouillon(s, b)
    return lu.ok ? null : lu.message.charAt(0).toUpperCase() + lu.message.slice(1)
  }

  return {
    edition, serveur, corps,
    compte: corps.cles.length + invalides,
    brouillon, modifie, erreur,
    changer: (s, b) => setEdition((e) => ({ brouillons: { ...e.brouillons, [s.key]: b }, remises: e.remises.filter((k) => k !== s.key) })),
    remettre: (s) => setEdition((e) => {
      const { [s.key]: _retire, ...reste } = e.brouillons
      return { brouillons: reste, remises: e.remises.includes(s.key) ? e.remises : [...e.remises, s.key] }
    }),
    annuler: (s) => setEdition((e) => {
      const { [s.key]: _retire, ...reste } = e.brouillons
      return { brouillons: reste, remises: e.remises.filter((k) => k !== s.key) }
    }),
    vider: () => setEdition(VIDE),
  }
}
